import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

type Source =
  | "crisis_hard"
  | "crisis_soft"
  | "deflection"
  | "thumbs_down"
  | "member_escalation";
type Status = "new" | "reviewed" | "resolved";

interface Row {
  id: string;
  source: Source;
  user_id: string | null;
  user_email: string | null;
  question: string | null;
  answer_excerpt: string | null;
  reference_table: string | null;
  reference_id: string | null;
  status: Status;
  created_at: string;
}

const SOURCE_LABEL: Record<Source, string> = {
  crisis_hard: "Crisis, hard stop",
  crisis_soft: "Crisis, soft flag",
  deflection: "Deflection",
  thumbs_down: "Thumbs down",
  member_escalation: "Member escalation",
};

const SOURCE_VARIANT: Record<
  Source,
  "default" | "secondary" | "destructive" | "outline"
> = {
  crisis_hard: "destructive",
  crisis_soft: "destructive",
  deflection: "outline",
  thumbs_down: "secondary",
  member_escalation: "default",
};

export default function EscalationsList() {
  const { toast } = useToast();
  const [status, setStatus] = useState<Status | "all">("new");
  const [sourceFilter, setSourceFilter] = useState<"all" | Source>("all");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from("escalation_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (status !== "all") q = q.eq("status", status);
    if (sourceFilter !== "all") q = q.eq("source", sourceFilter);
    const { data, error } = await q;
    setLoading(false);
    if (error) {
      toast({
        title: "Couldn't load escalations",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    setRows((data as Row[]) ?? []);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, sourceFilter]);

  const mark = async (id: string, next: Status) => {
    setBusy(id);
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id ?? null;
    const patch =
      next === "new"
        ? { status: next, reviewed_at: null, reviewed_by: null }
        : {
            status: next,
            reviewed_at: new Date().toISOString(),
            reviewed_by: uid,
          };
    const { error } = await supabase
      .from("escalation_log")
      .update(patch)
      .eq("id", id);
    setBusy(null);
    if (error) {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    void load();
  };

  const counts = useMemo(() => {
    return {
      total: rows.length,
    };
  }, [rows]);

  const referenceLink = (r: Row) => {
    if (!r.reference_id) return null;
    if (r.reference_table === "congregation_questions") {
      return (
        <Link
          to="/admin/questions"
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 inline-flex items-center gap-1"
        >
          Open ticket <ExternalLink className="h-3 w-3" />
        </Link>
      );
    }
    return (
      <span className="text-xs text-muted-foreground">
        {r.reference_table}
      </span>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={status}
          onValueChange={(v) => setStatus(v as Status | "all")}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={sourceFilter}
          onValueChange={(v) => setSourceFilter(v as any)}
        >
          <SelectTrigger className="w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="crisis_hard">Crisis, hard stop</SelectItem>
            <SelectItem value="crisis_soft">Crisis, soft flag</SelectItem>
            <SelectItem value="deflection">Deflection</SelectItem>
            <SelectItem value="thumbs_down">Thumbs down</SelectItem>
            <SelectItem value="member_escalation">Member escalation</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground ml-auto">
          {counts.total} shown
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading escalations
        </div>
      ) : rows.length === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground text-center">
          Nothing to review here.
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const open = !!expanded[r.id];
            return (
              <Card key={r.id} className="p-3">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={SOURCE_VARIANT[r.source]}>
                        {SOURCE_LABEL[r.source]}
                      </Badge>
                      <Badge variant="outline" className="capitalize">
                        {r.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </span>
                      {r.user_email && (
                        <span className="text-xs text-muted-foreground">
                          {r.user_email}
                        </span>
                      )}
                      {referenceLink(r)}
                    </div>
                    <div className="text-sm">
                      {r.question || (
                        <span className="text-muted-foreground italic">
                          No question text
                        </span>
                      )}
                    </div>
                    {open && r.answer_excerpt && (
                      <div className="text-xs text-muted-foreground border-l-2 border-border pl-2 mt-1 whitespace-pre-wrap">
                        {r.answer_excerpt}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() =>
                        setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))
                      }
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      {open ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-border/40">
                  {r.status !== "reviewed" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === r.id}
                      onClick={() => mark(r.id, "reviewed")}
                    >
                      Mark reviewed
                    </Button>
                  )}
                  {r.status !== "resolved" && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy === r.id}
                      onClick={() => mark(r.id, "resolved")}
                    >
                      Mark resolved
                    </Button>
                  )}
                  {r.status !== "new" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === r.id}
                      onClick={() => mark(r.id, "new")}
                    >
                      Reopen
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
